import {
  content,
  type DeckPassiveDefinition,
  type DeckPassiveId,
  type DeckPassivePresentation,
} from '@cards/cards';
import type { GameState, PlayerId } from '@cards/engine';

type DeckPassiveCatalog = ReadonlyMap<DeckPassiveId, Pick<
  DeckPassiveDefinition,
  'presentation'
>>;

/**
 * Resolves public presentation from an already-redacted snapshot.
 * Deliberately reads no vars, card instances or hidden player zones.
 */
export function deckPassivePresentationForPlayer(
  state: GameState,
  playerId: PlayerId,
  catalog: DeckPassiveCatalog = content.deckPassives,
): DeckPassivePresentation | undefined {
  const effect = state.effects
    .filter((candidate) => candidate.persistent === true
      && candidate.scope.kind === 'player'
      && candidate.scope.playerId === playerId
      && catalog.has(candidate.defId))
    .sort((left, right) => left.seq - right.seq)[0];
  return effect === undefined
    ? undefined
    : catalog.get(effect.defId)?.presentation;
}

/**
 * The catalog id of a player's deck passive, if any. Playback uses it to
 * attribute round-end heals, escalating damage, structure buffs and echoes to
 * the passive that caused them so the HUD badge can pulse in step.
 */
export function deckPassiveIdForPlayer(
  state: GameState,
  playerId: PlayerId,
  catalog: DeckPassiveCatalog = content.deckPassives,
): DeckPassiveId | undefined {
  const effect = state.effects
    .filter((candidate) => candidate.persistent === true
      && candidate.scope.kind === 'player'
      && candidate.scope.playerId === playerId
      && catalog.has(candidate.defId))
    .sort((left, right) => left.seq - right.seq)[0];
  return effect === undefined ? undefined : (effect.defId as DeckPassiveId);
}
